import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import { PortalProvider } from '@gorhom/portal';
import { theme } from '@sg/garnish';

import { UsableNetBanner } from '@order/usable-net';

import {
  OtaUpdateDialog,
  useResetDietaryRestrictionsOnLogout,
  useResetLoyaltyOffersOnLogout,
  useSyncDietaryRestrictions,
} from './components';
import { useCustomerTelemetry } from './context/Customer';
import { useTrackLastInteractedStore } from './context/LastInteractedStore';
import { useResetPlacedOrdersCounterOnLogout } from './context/PlacedOrdersCounter';
import {
  useAccessibilityTracking,
  useAppTrackingTransparency,
  useCancelLiveActivitiesOnLogout,
  useFetchUserCart,
  useHasUnseenChallenges,
  useLoyaltyPremiumStatus,
  useNetworkSubscription,
  useOrderStatusNotificationPreferences,
  usePushNotifications,
  useRegisterUrqlErrorTracker,
  useRegisterUrqlLogOutHandler,
  useResetSierraOnLogout,
} from './hooks';
import { useFacebookPixel } from './integrations/facebook-pixel';
import {
  useInitializeLiveActivities,
  useLiveActivityTelemetry,
} from './integrations/live-activities';
import { AppNavigation } from './navigation/AppNavigation';

// ────────────────────────────────────────────────────────────────────────────────

export const AppContent = memo(() => {
  useCustomerTelemetry();
  useFetchUserCart();
  useNetworkSubscription();
  usePushNotifications();
  useAppTrackingTransparency();
  useFacebookPixel();
  useTrackLastInteractedStore();
  useAccessibilityTracking();
  useRegisterUrqlErrorTracker();
  useRegisterUrqlLogOutHandler();
  useLiveActivityTelemetry();
  useSyncDietaryRestrictions();
  useInitializeLiveActivities();

  // ─── App Load Graphql Updates ────────────────────────────────────────

  useLoyaltyPremiumStatus({ requestPolicy: 'cache-and-network' });
  useHasUnseenChallenges('cache-and-network');
  useOrderStatusNotificationPreferences({ requestPolicy: 'cache-and-network' });

  // ─── Logout Resets ───────────────────────────────────────────────────

  useResetDietaryRestrictionsOnLogout();
  useResetPlacedOrdersCounterOnLogout();
  useResetLoyaltyOffersOnLogout();
  useResetSierraOnLogout();
  useCancelLiveActivitiesOnLogout();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View testID="app.container" style={styles.appContainer}>
      <UsableNetBanner />

      <PortalProvider>
        <OtaUpdateDialog />
        <AppNavigation />
      </PortalProvider>
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  appContainer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
});
